import React, { useCallback, useEffect, useState } from 'react';
import {
  ChevronDown, ChevronRight, Folder, FolderOpen, FolderTree,
  Info, Pencil, Plus, Trash2,
} from 'lucide-react';
import { staffApi } from '../lib/api';
import { Button } from './ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';
import { toast } from 'sonner';
import { FolderDialog } from './FolderDialog';

// Gestione centralizzata del template cartelle documenti staff (solo OPS/HR).
// La struttura è GLOBALE: ogni modifica si riflette automaticamente nel tab
// Documenti di TUTTI i dipendenti (collection `staff_folders`).
export const StaffFoldersManager = () => {
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedIds, setExpandedIds] = useState(null); // null = da inizializzare alla prima carica
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [parentForNew, setParentForNew] = useState(null);
  const [renameTarget, setRenameTarget] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await staffApi.listFolders();
      setFolders(data || []);
      // Prima carica: espandi tutto per dare visione completa del template
      setExpandedIds(prev => prev ?? new Set((data || []).map(f => f.folder_id)));
    } catch {
      toast.error('Errore nel caricamento delle cartelle');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleDelete() {
    if (!confirmDel) return;
    try {
      await staffApi.deleteFolder(confirmDel.folder_id);
      toast.success('Cartella eliminata');
      setConfirmDel(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore eliminazione cartella');
    }
  }

  function toggleExpand(folderId) {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(folderId)) next.delete(folderId);
      else next.add(folderId);
      return next;
    });
  }

  if (loading) return <div className="py-12"><div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin mx-auto" /></div>;

  // Albero: gruppo per parent_folder_id
  const byParent = {};
  folders.forEach(f => {
    const key = f.parent_folder_id || '__root__';
    if (!byParent[key]) byParent[key] = [];
    byParent[key].push(f);
  });
  Object.keys(byParent).forEach(k => {
    byParent[k].sort((a, b) => {
      if (a.is_year_root && b.is_year_root) return (b.year || 0) - (a.year || 0);
      return a.name.localeCompare(b.name, 'it');
    });
  });

  function renderTree(parentKey, depth = 0) {
    const children = byParent[parentKey] || [];
    if (!children.length) return null;
    return (
      <ul className="space-y-0.5">
        {children.map(f => {
          const hasKids = !!(byParent[f.folder_id]?.length);
          const isExpanded = expandedIds?.has(f.folder_id);
          return (
            <li key={f.folder_id} className="select-none">
              <div className="flex items-center gap-1.5 rounded-sm pr-1 py-1 hover:bg-slate-50" data-testid={`tpl-folder-row-${f.folder_id}`}>
                <button
                  type="button"
                  onClick={() => hasKids && toggleExpand(f.folder_id)}
                  className={`w-5 h-5 flex items-center justify-center shrink-0 ${hasKids ? 'text-slate-500 hover:text-slate-900' : 'opacity-0 pointer-events-none'}`}
                  data-testid={`tpl-folder-toggle-${f.folder_id}`}
                >
                  {hasKids && (isExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />)}
                </button>
                {isExpanded && hasKids
                  ? <FolderOpen className="w-4 h-4 shrink-0 text-slate-500" />
                  : <Folder className="w-4 h-4 shrink-0 text-slate-500" />}
                <span className="text-sm text-slate-900 font-medium truncate">{f.name}</span>
                {f.is_year_root && (
                  <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 border rounded-sm bg-slate-50 text-slate-500 border-slate-200 shrink-0">
                    Anno
                  </span>
                )}
                {f.doc_count > 0 && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-600 shrink-0" title="Documenti in questa cartella (tutti i dipendenti)">
                    {f.doc_count} doc
                  </span>
                )}
                <div className="ml-auto flex gap-0.5 shrink-0">
                  <button
                    type="button"
                    onClick={() => { setParentForNew(f); setShowNewFolder(true); }}
                    className="p-1.5 rounded hover:bg-slate-200 text-slate-600"
                    title="Nuova sotto-cartella"
                    data-testid={`tpl-folder-add-child-${f.folder_id}`}
                  ><Plus className="w-3.5 h-3.5" /></button>
                  {!f.is_year_root && (
                    <>
                      <button
                        type="button"
                        onClick={() => setRenameTarget(f)}
                        className="p-1.5 rounded hover:bg-slate-200 text-slate-600"
                        title="Rinomina"
                        data-testid={`tpl-folder-rename-${f.folder_id}`}
                      ><Pencil className="w-3.5 h-3.5" /></button>
                      <button
                        type="button"
                        onClick={() => setConfirmDel(f)}
                        className="p-1.5 rounded hover:bg-red-50 text-red-600"
                        title="Elimina"
                        data-testid={`tpl-folder-delete-${f.folder_id}`}
                      ><Trash2 className="w-3.5 h-3.5" /></button>
                    </>
                  )}
                </div>
              </div>
              {isExpanded && hasKids && (
                <div className="ml-5 border-l border-slate-200 pl-1">
                  {renderTree(f.folder_id, depth + 1)}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <div className="space-y-4 max-w-3xl" data-testid="staff-folders-manager">
      {/* Banner spiegazione propagazione */}
      <div className="bg-blue-50 border border-blue-200 rounded-sm p-3 flex items-start gap-2.5" data-testid="folders-info-banner">
        <Info className="w-4 h-4 text-blue-700 shrink-0 mt-0.5" />
        <div className="text-xs text-blue-900 leading-relaxed">
          <p className="font-semibold">Struttura condivisa per tutti i dipendenti</p>
          <p className="mt-0.5">
            Le cartelle definite qui sono il template unico dei documenti staff: ogni modifica
            (creazione, rinomina, eliminazione) si propaga automaticamente al tab Documenti di
            <b> tutti</b> i dipendenti. Le cartelle anno (es. 2026) vengono create in automatico
            e non sono modificabili. Una cartella può essere eliminata solo se vuota.
          </p>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <FolderTree className="w-4 h-4 text-slate-500" />
            <p className="text-sm font-semibold text-slate-900">Template cartelle documenti</p>
          </div>
          <Button
            size="sm"
            onClick={() => { setParentForNew(null); setShowNewFolder(true); }}
            className="gap-1.5"
            data-testid="tpl-folder-add-root"
          >
            <Plus className="w-3.5 h-3.5" /> Nuova cartella
          </Button>
        </div>
        <div className="p-3">
          {folders.length === 0 ? (
            <div className="py-10 text-center" data-testid="tpl-folders-empty">
              <FolderTree className="w-10 h-10 mx-auto text-slate-300 mb-2" />
              <p className="text-slate-600 text-sm font-medium">Nessuna cartella definita</p>
              <p className="text-slate-500 text-xs mt-1">Crea la prima cartella del template.</p>
            </div>
          ) : (
            renderTree('__root__')
          )}
        </div>
      </div>

      {showNewFolder && (
        <FolderDialog
          mode="create"
          parent={parentForNew}
          onClose={() => { setShowNewFolder(false); setParentForNew(null); }}
          onSaved={() => { setShowNewFolder(false); setParentForNew(null); load(); }}
        />
      )}

      {renameTarget && (
        <FolderDialog
          mode="rename"
          folder={renameTarget}
          onClose={() => setRenameTarget(null)}
          onSaved={() => { setRenameTarget(null); load(); }}
        />
      )}

      <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Elimina cartella</AlertDialogTitle>
            <AlertDialogDescription>
              Vuoi davvero eliminare <b>{confirmDel?.name}</b> dal template? La cartella sparirà
              per tutti i dipendenti. Deve essere vuota — se contiene documenti o sotto-cartelle,
              l'eliminazione fallirà.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="tpl-folder-delete-confirm">
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
